import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';

export const CONFIDENCE_QUERY_KEY = 'confidence-control';

export type ConfidenceSettings = {
  timerBasis: 'actualStart' | 'plannedWallClock' | 'actualStartWithDrift';
  wrapUpMinutes: number;
  finalMinutes: number;
  flashOnOvertime: boolean;
  adjustmentStrategy: 'sectionOnly' | 'suggest' | 'absorbNext' | 'spreadRemaining';
  autoMessages: { wrapUp: boolean; final: boolean; timeUp: boolean; lastSlide: boolean };
  qaForTalks: boolean;
  qaMinutes: number;
};

export type ConfidenceControl = {
  settings: ConfidenceSettings;
  activeMessage: { text: string; expiresAt?: string } | null;
  timerOverrides: Record<string, { durationSec: number; setAt: string }>;
  thresholdOverrides: Record<string, { wrapUpMinutes?: number; finalMinutes?: number }>;
  qaOverrides: Record<string, { enabled: boolean; minutes: number }>;
  aheadBehindSec: number;
  timer: {
    basis: ConfidenceSettings['timerBasis'];
    startedAt?: string;
    durationSec?: number;
    elapsedSec?: number;
    wrapUpMinutes: number;
    finalMinutes: number;
    serverNow: string;
    plannedEndClock?: string;
    plannedEndDate?: string;
    plannedEndAt?: string;
    plannedStartAt?: string;
  } | null;
  pendingSuggestion: { createdAt: string; overrides: Record<string, number>; summary: string } | null;
  systemPresets: { key: string; text: string; source: 'system' }[];
  orgPresets: { id: string; text: string }[];
  organizationId: string | null;
};

type Entity = { entityType: 'Flow' | 'Space'; entityId: string };

async function request<T>(
  token: string,
  path: string,
  method: (typeof METHOD)[keyof typeof METHOD],
  body?: unknown,
): Promise<T> {
  return sendJSONRequest({
    url: `${import.meta.env.VITE_API_URL}/player/confidence${path}`,
    method,
    token,
    body,
  }) as Promise<T>;
}

export function useConfidenceControlQuery(entity: Entity | undefined) {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: [CONFIDENCE_QUERY_KEY, entity?.entityType, entity?.entityId],
    enabled: Boolean(entity?.entityId),
    refetchInterval: 8000,
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams({
        entityType: entity!.entityType,
        entityId: entity!.entityId,
      });
      return request<ConfidenceControl>(token ?? '', `?${params.toString()}`, METHOD.GET);
    },
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return (entity: Entity) => {
    void queryClient.invalidateQueries({ queryKey: [CONFIDENCE_QUERY_KEY, entity.entityType, entity.entityId] });
  };
}

export function useUpdateConfidenceSettingsMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: Entity & Partial<ConfidenceSettings>) => {
      const token = await getToken();
      return request<ConfidenceSettings>(token ?? '', '/settings', METHOD.PATCH, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useConfidenceMessageMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: Entity & { text: string; presetKey?: string; hideAfterSec?: number }) => {
      const token = await getToken();
      return request(token ?? '', '/message', METHOD.POST, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useClearConfidenceMessageMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: Entity) => {
      const token = await getToken();
      return request(token ?? '', '/message/clear', METHOD.POST, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useAdjustConfidenceTimeMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (
      body: Entity & {
        slotId: string;
        mode: 'remaining' | 'fromStart' | 'untilClock';
        remainingSec?: number;
        totalFromStartSec?: number;
        untilClock?: string;
        nowMinutes?: number;
      },
    ) => {
      const token = await getToken();
      return request(token ?? '', '/time', METHOD.POST, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useSetConfidenceQaMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: Entity & { slotId: string; enabled: boolean; minutes: number }) => {
      const token = await getToken();
      return request(token ?? '', '/qa', METHOD.POST, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useResetConfidenceQaMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: Entity) => {
      const token = await getToken();
      return request(token ?? '', '/qa/reset', METHOD.POST, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useConfidenceThresholdsMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: Entity & { slotId: string; wrapUpMinutes?: number; finalMinutes?: number }) => {
      const token = await getToken();
      return request(token ?? '', '/thresholds', METHOD.POST, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useConfirmConfidenceSuggestionMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: Entity) => {
      const token = await getToken();
      return request(token ?? '', '/suggestion/confirm', METHOD.POST, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useDismissConfidenceSuggestionMutation() {
  const { getToken } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: Entity) => {
      const token = await getToken();
      return request(token ?? '', '/suggestion/dismiss', METHOD.POST, body);
    },
    onSuccess: (_, variables) => invalidate(variables),
  });
}

export function useOrgConfidencePresetsQuery(organizationId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ['confidence-org-presets', organizationId],
    enabled: Boolean(organizationId),
    queryFn: async () => {
      const token = await getToken();
      return request<{ presets: { id: string; text: string }[] }>(
        token ?? '',
        `/org-presets/${organizationId}`,
        METHOD.GET,
      );
    },
  });
}

export function useSaveOrgConfidencePresetsMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { organizationId: string; presets: { id?: string; text: string }[] }) => {
      const token = await getToken();
      return request<{ presets: { id: string; text: string }[] }>(token ?? '', '/org-presets', METHOD.PUT, body);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['confidence-org-presets', variables.organizationId] });
    },
  });
}
